import isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';

import { loadUser } from '../../../utils';
import { getRoleIdByName } from '../../Person/utils';

import { roleNames } from '../../Person/constants';
import {
  ACTIVE_THREAD_FILE,
  ACTIVE_THREAD_INPUT,
  ACTIVE_THREAD_TITLE,
} from '../constants';

import StyledLink from '../../StyledLink';

export const getLinkToProperty = (person, buildings) => {
  if (!person || isEmpty(person)) {
    return '-';
  }

  const buildingId = person.building;
  const unitId = person.unit;
  const buildingName =
    buildingId &&
    buildings &&
    buildings[buildingId] &&
    buildings[buildingId].name;
  const unitName = unitId && person.propertyName;
  if (buildingName && unitName) {
    return (
      <>
        <StyledLink to={`/building/${buildingId}`}>{buildingName}</StyledLink>
        &nbsp;-&nbsp;
        <StyledLink to={`/building/${buildingId}/units/${unitId}`}>
          {unitName}
        </StyledLink>
      </>
    );
  }
  if (buildingName && !unitName) {
    return (
      <StyledLink to={`/building/${buildingId}`}>{buildingName}</StyledLink>
    );
  }
  return person.propertyName ? person.propertyName : '';
};

export const getLinkToUser = (person, isUrlOnly = false) => {
  if (!person) {
    return null;
  }
  let url;
  const name = `${person.firstName} ${person.lastName}`;
  const { detailsAvailable } = person;
  if (!detailsAvailable) return null;
  switch (person.role) {
    case getRoleIdByName(roleNames.handyman):
    case getRoleIdByName(roleNames.superintendent):
    case getRoleIdByName(roleNames.concierge):
    case getRoleIdByName(roleNames.property_manager):
    case getRoleIdByName(roleNames.pmc_owner):
      url = person.building
        ? `/building/${person.building}/staff/${person.id}`
        : `/pmc/${person.pmc}/staff/${person.id}`;
      break;
    case getRoleIdByName(roleNames.tenant):
    case getRoleIdByName(roleNames.owner):
    case getRoleIdByName(roleNames.board_member):
      url = `/building/${person.building}/residents/${person.id}`;
      break;
    // it doesn't work for visitors and brokers - we need their role id but have and only person id
    case getRoleIdByName(roleNames.visitor):
    case getRoleIdByName(roleNames.broker):
    case getRoleIdByName(roleNames.prospective_tenant):
      // url = `/visitors/profile/${person.id}`;
      url = `/global-person-search/${person.id}`;
      break;
    default:
      url = null;
      return false;
  }
  const currentUser = loadUser();
  if (currentUser && currentUser.id === person.id) {
    url = `/global-person-search/profile`;
  }
  if (!url) return null;
  if (isUrlOnly && url) return url;
  return url && name ? <StyledLink to={url}>{name}</StyledLink> : name;
};

export const makeResizableElement = (elLeft, elRight, resizer) => {
  const elementLeft = document.querySelector(elLeft);
  const elementRight = document.querySelector(elRight);
  const resizers = document.querySelector(resizer);

  if (!elementLeft || !elementRight || !resizers) return;

  const minimum_size = 400;
  let original_width_l = 0;
  let original_width_r = 0;
  let original_x_r = 0;
  let original_mouse_x = 0;
  let width_l;
  let width_r;

  const throttledResize = throttle(resize, 32);

  const currentResizer = resizers;
  currentResizer?.addEventListener('mousedown', (e) => {
    e.preventDefault();
    original_width_l = parseFloat(
      getComputedStyle(elementLeft, null)
        .getPropertyValue('width')
        .replace('px', ''),
    );
    original_width_r = parseFloat(
      getComputedStyle(elementRight, null)
        .getPropertyValue('width')
        .replace('px', ''),
    );
    original_x_r = elementRight.getBoundingClientRect().left;
    original_mouse_x = e.pageX;
    window.addEventListener('mousemove', throttledResize);
    window.addEventListener('mouseup', stopResize);
  });

  function resize(e) {
    if (currentResizer.classList.contains('left-right')) {
      width_l = original_width_l + (e.pageX - original_mouse_x);
      width_r = original_width_r - (e.pageX - original_mouse_x);
      if (width_r > minimum_size) {
        elementLeft.style.width = `${width_l}px`;
        elementRight.style.width = `${width_r}px`;
        elementRight.style.left = `${
          original_x_r + (e.pageX - original_mouse_x)
        }px`;
      }
    }
  }

  function stopResize() {
    window.removeEventListener('mousemove', throttledResize);
  }
};

export const enableZoomDetection = () => {
  const oldresize = window.onresize;
  window.onresize = (e) => {
    const event = window.event || e;
    if (typeof oldresize === 'function' && !oldresize.call(window, event)) {
      return false;
    }
    if (typeof window.onzoom === 'function') {
      return window.onzoom.call(window, event);
    }
    return null;
  };
};

export const getContainerHeight = () => {
  const vh = Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0,
  );
  return vh ? vh - 160 : 750;
};

export const getMessagesHeight = () => {
  const containerHeight = getContainerHeight();
  const messagesInputHeight =
    document.getElementById('messages_input_container')?.scrollHeight || 0;
  return containerHeight - 260 - messagesInputHeight;
};

export const getMessagesHeightMobile = () => {
  const parentHeight = getContainerHeight();
  const childrenList = [
    ACTIVE_THREAD_TITLE,
    ACTIVE_THREAD_FILE,
    ACTIVE_THREAD_INPUT,
  ];
  const childrenHeight = childrenList.reduce((prevHeight, elementId) => {
    const height = document.getElementById(elementId)?.offsetHeight || 0;
    return prevHeight + height;
  }, 0);

  return parentHeight - childrenHeight;
};

export const checkMessage = (message) =>
  message ? /^\s*$/.test(message) : true;
